// Inbox — desktop: 2-column master-detail. Mobile: list view by default,
// tap message → detail view replaces list, back button returns.

import { useEffect, useState, type ChangeEvent } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { listInbound, getInbound, markInboundRead, archiveInbound, replyInbound, backfillInbound, type EmailAttachment } from "@/lib/api";
import { fmtRelative, fmtDateTime } from "@/lib/formatters";

type AttachmentState = EmailAttachment & { key: string; size: number };

const MAX_ATTACHMENT_COUNT = 5;
const MAX_ATTACHMENT_BYTES = 8 * 1024 * 1024;

function formatBytes(bytes: number): string {
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1).replace(".", ",")} MB`;
}

function readAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result ?? "");
      resolve(result.includes(",") ? result.split(",")[1] : result);
    };
    reader.onerror = () => reject(new Error(`${file.name} kon niet worden gelezen`));
    reader.readAsDataURL(file);
  });
}

function attachmentPayloads(attachments: AttachmentState[]): EmailAttachment[] {
  return attachments.map(({ filename, content, content_type }) => ({ filename, content, content_type }));
}

export function InboxIndex() {
  const qc = useQueryClient();
  const [unreadOnly, setUnreadOnly] = useState(true);
  const [q, setQ] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [replyOpen, setReplyOpen] = useState(false);
  const [replyText, setReplyText] = useState("");
  const [replyAttachments, setReplyAttachments] = useState<AttachmentState[]>([]);
  const [toast, setToast] = useState<string | null>(null);

  const { data: items, isLoading } = useQuery({
    queryKey: ["inbox", unreadOnly, q],
    queryFn: () => listInbound({ unread: unreadOnly || undefined, q: q || undefined, limit: 200 }),
  });

  const { data: detail } = useQuery({
    queryKey: ["inbox-item", selectedId],
    queryFn: () => getInbound(selectedId!),
    enabled: !!selectedId,
  });

  const readMut = useMutation({
    mutationFn: ({ id, read }: { id: string; read: boolean }) => markInboundRead(id, read),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["inbox"] });
      qc.invalidateQueries({ queryKey: ["inbox-unread"] });
    },
  });

  const archiveMut = useMutation({
    mutationFn: (id: string) => archiveInbound(id),
    onSuccess: () => {
      setSelectedId(null);
      qc.invalidateQueries({ queryKey: ["inbox"] });
      qc.invalidateQueries({ queryKey: ["inbox-unread"] });
    },
  });

  const backfillMut = useMutation({
    mutationFn: (id?: string) => backfillInbound(id ? { id } : { limit: 50 }),
    onSuccess: (r) => {
      setToast(`Backfill: ${r.updated}/${r.processed} ververst${r.failed ? `, ${r.failed} mislukt` : ""}`);
      qc.invalidateQueries({ queryKey: ["inbox"] });
      qc.invalidateQueries({ queryKey: ["inbox-item", selectedId] });
    },
    onError: (e: Error) => setToast(`Fout: ${e.message}`),
  });

  const replyMut = useMutation({
    mutationFn: ({ id, body, attachments }: { id: string; body: string; attachments: AttachmentState[] }) =>
      replyInbound(id, { body_text: body, attachments: attachmentPayloads(attachments) }),
    onSuccess: () => {
      setToast("Antwoord verzonden");
      setReplyOpen(false);
      setReplyText("");
      setReplyAttachments([]);
      qc.invalidateQueries({ queryKey: ["inbox"] });
      qc.invalidateQueries({ queryKey: ["inbox-item", selectedId] });
      qc.invalidateQueries({ queryKey: ["email-sends"] });
    },
    onError: (e: Error) => setToast(`Fout: ${e.message}`),
  });

  useEffect(() => {
    setReplyOpen(false);
    setReplyText("");
    setReplyAttachments([]);
  }, [selectedId]);

  const handleAttachmentFiles = async (event: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []);
    event.target.value = "";
    if (!files.length) return;

    const nextRawBytes = replyAttachments.reduce((sum, a) => sum + a.size, 0) + files.reduce((sum, f) => sum + f.size, 0);
    if (replyAttachments.length + files.length > MAX_ATTACHMENT_COUNT) {
      setToast(`Maximaal ${MAX_ATTACHMENT_COUNT} bijlagen per antwoord`);
      return;
    }
    if (nextRawBytes > MAX_ATTACHMENT_BYTES) {
      setToast(`Bijlagen zijn samen te groot (max. ${formatBytes(MAX_ATTACHMENT_BYTES)})`);
      return;
    }

    try {
      const next = await Promise.all(files.map(async (file) => ({
        key: `${file.name}-${file.size}-${file.lastModified}`,
        filename: file.name,
        content: await readAsBase64(file),
        content_type: file.type || undefined,
        size: file.size,
      })));
      setReplyAttachments((current) => [...current, ...next]);
    } catch (err) {
      setToast(err instanceof Error ? err.message : "Bijlage kon niet worden gelezen");
    }
  };

  const removeAttachment = (key: string) => {
    setReplyAttachments((current) => current.filter((attachment) => attachment.key !== key));
  };

  const onSelect = (id: string, isUnread: boolean) => {
    setSelectedId(id);
    if (isUnread) readMut.mutate({ id, read: true });
  };

  const showingDetailMobile = !!selectedId; // controls master/detail visibility on mobile

  return (
    <>
      <div
        className="topbar-actions inbox-toolbar"
        style={{ marginBottom: "1rem" }}
      >
        <input
          className="search"
          placeholder="🔍 Zoek afzender of onderwerp…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <label className="inbox-unread-toggle">
          <input type="checkbox" checked={unreadOnly} onChange={(e) => setUnreadOnly(e.target.checked)} />
          Alleen ongelezen
        </label>
      </div>

      <div className={`inbox-grid ${showingDetailMobile ? "show-detail" : ""}`}>
        {/* MASTER — list */}
        <div className="panel inbox-list-panel">
          <div className="panel-h">
            <h3>Inbox {items && <span className="badge">{items.length}</span>}</h3>
          </div>
          {isLoading && <div className="empty"><span className="spinner" /> Laden…</div>}
          {!isLoading && (items ?? []).length === 0 && (
            <div className="empty">
              {unreadOnly ? "Geen ongelezen mail." : "Inbox is leeg."}
              <br /><small style={{ opacity: 0.6 }}>Inbound parsing moet aan staan in Resend dashboard.</small>
            </div>
          )}
          <div className="inbox-list">
            {(items ?? []).map((m) => {
              const unread = !m.read_at;
              const active = selectedId === m.id;
              return (
                <button
                  key={m.id}
                  onClick={() => onSelect(m.id, unread)}
                  className={`inbox-item ${active ? "active" : ""} ${unread ? "unread" : ""}`}
                >
                  <div className="inbox-item-row1">
                    <span className="inbox-from">
                      {unread && <span className="inbox-dot">●</span>}
                      {m.from_name ?? m.from_email}
                    </span>
                    <small className="inbox-time">{fmtRelative(m.received_at)}</small>
                  </div>
                  <div className="inbox-subject">
                    {m.subject ?? <span style={{ opacity: 0.5 }}>(geen onderwerp)</span>}
                  </div>
                  <div className="inbox-meta">
                    {m.order && <small>↳ <code>{m.order.order_no}</code></small>}
                    {m.replied_at && <small className="inbox-replied">↩ beantwoord</small>}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* DETAIL */}
        <div className="panel inbox-detail-panel">
          {!selectedId && <div className="empty hide-on-mobile">Selecteer een bericht.</div>}
          {selectedId && !detail && <div className="empty"><span className="spinner" /> Laden…</div>}
          {selectedId && detail && (
            <>
              <div className="inbox-detail-head">
                <button
                  type="button"
                  className="inbox-back"
                  onClick={() => setSelectedId(null)}
                  aria-label="Terug naar inbox"
                >
                  ← Terug
                </button>
                <div className="inbox-detail-title">
                  <h3>{detail.subject ?? "(geen onderwerp)"}</h3>
                  <div className="inbox-from-line">
                    <strong>{detail.from_name ?? detail.from_email}</strong>
                    {detail.from_name && <small> &lt;{detail.from_email}&gt;</small>}
                  </div>
                  <small className="inbox-meta-line">
                    Aan {detail.to_email} · {fmtDateTime(detail.received_at)}
                  </small>
                </div>
                <div className="inbox-detail-actions">
                  <button
                    className="btn-admin sm"
                    onClick={() => {
                      setReplyOpen((v) => {
                        if (v) {
                          setReplyText("");
                          setReplyAttachments([]);
                        }
                        return !v;
                      });
                    }}
                  >
                    ↩ {replyOpen ? "Sluit antwoord" : "Antwoord"}
                  </button>
                  {!detail.text_body && !detail.html_body && detail.resend_id && (
                    <button
                      className="btn-mini"
                      onClick={() => backfillMut.mutate(detail.id)}
                      disabled={backfillMut.isPending}
                      title="Body opnieuw ophalen via Resend Receiving API"
                    >
                      ↻ Inhoud
                    </button>
                  )}
                  <button
                    className="btn-mini"
                    onClick={() => readMut.mutate({ id: detail.id, read: !detail.read_at })}
                  >
                    {detail.read_at ? "Ongelezen" : "Gelezen"}
                  </button>
                  <button
                    className="btn-mini"
                    onClick={() => {
                      if (confirm("Bericht archiveren?")) archiveMut.mutate(detail.id);
                    }}
                  >
                    Archiveer
                  </button>
                </div>
              </div>

              {detail.replied_at && (
                <div className="inbox-callout inbox-callout-orange">
                  ↩ Beantwoord op {fmtDateTime(detail.replied_at)}
                  {detail.reply_email_send_id && (
                    <> · <Link to={`/emails/${detail.reply_email_send_id}`}>bekijk verzonden mail</Link></>
                  )}
                </div>
              )}

              {replyOpen && (
                <div className="inbox-reply-panel">
                  <div className="inbox-reply-meta">
                    <strong>Aan:</strong> {detail.from_email}
                    <br />
                    <strong>Onderwerp:</strong> {detail.subject?.toLowerCase().startsWith("re:") ? detail.subject : `Re: ${detail.subject ?? ""}`}
                  </div>
                  <textarea
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    placeholder="Typ je antwoord…"
                    rows={6}
                    className="inbox-reply-textarea"
                  />
                  <div className="attachment-box inbox-reply-attachments">
                    <div className="attachment-head">
                      <strong>Bijlagen</strong>
                      <small>{replyAttachments.length}/{MAX_ATTACHMENT_COUNT} · max {formatBytes(MAX_ATTACHMENT_BYTES)}</small>
                    </div>
                    <label className="attachment-add">
                      📎 Voeg bestand toe
                      <input type="file" multiple onChange={handleAttachmentFiles} />
                    </label>
                    {replyAttachments.length > 0 && (
                      <div className="attachment-list">
                        {replyAttachments.map((attachment) => (
                          <div key={attachment.key} className="attachment-row">
                            <span>
                              <strong>{attachment.filename}</strong>
                              <small>{formatBytes(attachment.size)}</small>
                            </span>
                            <button type="button" onClick={() => removeAttachment(attachment.key)}>Verwijder</button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="inbox-reply-actions">
                    <button
                      className="btn-mini"
                      onClick={() => { setReplyOpen(false); setReplyText(""); setReplyAttachments([]); }}
                      disabled={replyMut.isPending}
                    >
                      Annuleer
                    </button>
                    <button
                      className="btn-admin sm"
                      disabled={replyMut.isPending || (!replyText.trim() && replyAttachments.length === 0)}
                      onClick={() => replyMut.mutate({ id: detail.id, body: replyText, attachments: replyAttachments })}
                    >
                      {replyMut.isPending ? "Verzenden…" : "📧 Verstuur"}
                    </button>
                  </div>
                </div>
              )}

              {detail.order && (
                <div className="inbox-callout">
                  ↳ Gekoppeld aan bestelling{" "}
                  <Link to={`/orders/${(detail.order as { id: string }).id}`}>
                    <code>{(detail.order as { order_no: string }).order_no}</code>
                  </Link>
                </div>
              )}

              {detail.original && (
                <div className="inbox-callout">
                  Reply op:{" "}
                  <Link to={`/emails/${(detail.original as { id: string }).id}`}>
                    {(detail.original as { subject: string }).subject ?? "—"}
                  </Link>
                </div>
              )}

              <div className="inbox-body">
                {detail.html_body ? (
                  <iframe
                    srcDoc={detail.html_body}
                    className="inbox-iframe"
                    sandbox=""
                    title="email body"
                  />
                ) : (
                  <pre className="inbox-text">
                    {detail.text_body ?? "(geen inhoud)"}
                  </pre>
                )}
              </div>

              {(detail.attachments as Array<{ filename?: string; size?: number; url?: string }>).length > 0 && (
                <div className="inbox-attachments">
                  <strong>Bijlagen</strong>
                  <ul>
                    {(detail.attachments as Array<{ filename?: string; size?: number; url?: string }>).map((a, i) => (
                      <li key={i}>
                        {a.url ? <a href={a.url} target="_blank" rel="noreferrer">{a.filename ?? `bijlage ${i + 1}`}</a> : (a.filename ?? `bijlage ${i + 1}`)}
                        {a.size && <small> ({Math.round(a.size / 1024)} KB)</small>}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {toast && <div className="toast" onClick={() => setToast(null)}>{toast}</div>}
    </>
  );
}
